import { UCUM_CATEGORIES } from "../constants/ucumUnits";

/**
 * Formats a Unix timestamp to a human-readable datetime string
 * @param timestamp The timestamp value
 * @param unit The timestamp unit (unix_s, unix_ms, unix_us, unix_ns)
 * @param options Formatting options
 * @returns Formatted datetime string
 */
function formatTimestamp(timestamp: number, unit: string, options?: { 
    short?: boolean; 
    includeTime?: boolean;
    includeMillis?: boolean;
}): string {
    const opts = { short: false, includeTime: true, includeMillis: false, ...options };
    
    // Convert to milliseconds based on unit
    let timestampMs: number;
    switch (unit) {
        case 'unix_s':
            timestampMs = timestamp * 1000;
            break;
        case 'unix_ms':
            timestampMs = timestamp;
            break;
        case 'unix_us':
            timestampMs = timestamp / 1000;
            break;
        case 'unix_ns':
            timestampMs = timestamp / 1000000;
            break;
        default:
            timestampMs = timestamp;
    }
    
    const date = new Date(timestampMs);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
        console.error(`Invalid timestamp value: ${timestamp} (unit: ${unit}, converted to ms: ${timestampMs})`);
        return 'Invalid Date';
    }
    
    if (opts.short) {
        // Short format for axis labels
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const isThisYear = date.getFullYear() === now.getFullYear();
        
        if (isToday) {
            return date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                second: opts.includeMillis ? '2-digit' : undefined,
                fractionalSecondDigits: opts.includeMillis ? 3 : undefined
            });
        } else if (isThisYear) {
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                hour: opts.includeTime ? '2-digit' : undefined,
                minute: opts.includeTime ? '2-digit' : undefined
            });
        } else {
            return date.toLocaleDateString('en-US', { 
                year: '2-digit',
                month: 'short', 
                day: 'numeric'
            });
        }
    } else {
        // Full format for tooltips
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: opts.includeTime ? '2-digit' : undefined,
            minute: opts.includeTime ? '2-digit' : undefined,
            second: opts.includeTime ? '2-digit' : undefined,
            fractionalSecondDigits: opts.includeMillis ? 3 : undefined,
            timeZoneName: 'short'
        });
    }
}

export interface ConvertedValue {
    value: number;
    unit: string;
    displayName: string;
    isTimestamp?: boolean;
}

// Define conversion factors and hierarchies for different unit types
const UNIT_HIERARCHIES = {
    // Information units (decimal - SI prefixes, 1000-based)
    information_decimal: [
        { unit: "bit", factor: 1, displayName: "Bits" },
        { unit: "By", factor: 8, displayName: "Bytes" },
        { unit: "kBy", factor: 8000, displayName: "Kilobytes" },
        { unit: "MBy", factor: 8000000, displayName: "Megabytes" },
        { unit: "GBy", factor: 8000000000, displayName: "Gigabytes" },
        { unit: "TBy", factor: 8000000000000, displayName: "Terabytes" },
        { unit: "PBy", factor: 8000000000000000, displayName: "Petabytes" },
    ],
    
    // Information units (binary - IEC prefixes, 1024-based)
    information_binary: [
        { unit: "By", factor: 1, displayName: "Bytes" },
        { unit: "KiBy", factor: 1024, displayName: "Kibibytes" },
        { unit: "MiBy", factor: 1024 * 1024, displayName: "Mebibytes" },
        { unit: "GiBy", factor: 1024 * 1024 * 1024, displayName: "Gibibytes" },
        { unit: "TiBy", factor: 1024 * 1024 * 1024 * 1024, displayName: "Tebibytes" },
        { unit: "PiBy", factor: 1024 * 1024 * 1024 * 1024 * 1024, displayName: "Pebibytes" },
    ],
    
    // Data rate units (bits per second)
    data_rate_bits: [
        { unit: "bit/s", factor: 1, displayName: "Bits/Second" },
        { unit: "kbit/s", factor: 1000, displayName: "Kilobits/Second" },
        { unit: "Mbit/s", factor: 1000000, displayName: "Megabits/Second" },
        { unit: "Gbit/s", factor: 1000000000, displayName: "Gigabits/Second" },
    ],
    
    // Data rate units (bytes per second - decimal)
    data_rate_bytes: [
        { unit: "By/s", factor: 1, displayName: "Bytes/Second" },
        { unit: "kBy/s", factor: 1000, displayName: "Kilobytes/Second" },
        { unit: "MBy/s", factor: 1000000, displayName: "Megabytes/Second" },
        { unit: "GBy/s", factor: 1000000000, displayName: "Gigabytes/Second" },
    ],
    
    // Data rate units (bytes per second - binary)
    data_rate_bytes_binary: [
        { unit: "By/s", factor: 1, displayName: "Bytes/Second" },
        { unit: "KiBy/s", factor: 1024, displayName: "Kibibytes/Second" },
        { unit: "MiBy/s", factor: 1024 * 1024, displayName: "Mebibytes/Second" },
        { unit: "GiBy/s", factor: 1024 * 1024 * 1024, displayName: "Gibibytes/Second" },
        { unit: "TiBy/s", factor: 1024 * 1024 * 1024 * 1024, displayName: "Tebibytes/Second" },
    ],
    
    // Time units
    time: [
        { unit: "ns", factor: 0.000000001, displayName: "Nanoseconds" },
        { unit: "us", factor: 0.000001, displayName: "Microseconds" },
        { unit: "ms", factor: 0.001, displayName: "Milliseconds" },
        { unit: "s", factor: 1, displayName: "Seconds" },
        { unit: "min", factor: 60, displayName: "Minutes" },
        { unit: "h", factor: 3600, displayName: "Hours" },
        { unit: "d", factor: 86400, displayName: "Days" },
        { unit: "wk", factor: 604800, displayName: "Weeks" },
        { unit: "mo", factor: 2592000, displayName: "Months" }, // ~30 days
        { unit: "a", factor: 31536000, displayName: "Years" }, // 365 days
    ],
    
    // Length units (metric)
    length_metric: [
        { unit: "nm", factor: 0.000000001, displayName: "Nanometers" },
        { unit: "um", factor: 0.000001, displayName: "Micrometers" },
        { unit: "mm", factor: 0.001, displayName: "Millimeters" },
        { unit: "cm", factor: 0.01, displayName: "Centimeters" },
        { unit: "dm", factor: 0.1, displayName: "Decimeters" },
        { unit: "m", factor: 1, displayName: "Meters" },
        { unit: "km", factor: 1000, displayName: "Kilometers" },
    ],
    
    // Mass units (metric)
    mass_metric: [
        { unit: "ng", factor: 0.000000001, displayName: "Nanograms" },
        { unit: "ug", factor: 0.000001, displayName: "Micrograms" },
        { unit: "mg", factor: 0.001, displayName: "Milligrams" },
        { unit: "g", factor: 1, displayName: "Grams" },
        { unit: "kg", factor: 1000, displayName: "Kilograms" },
        { unit: "t", factor: 1000000, displayName: "Metric Tons" },
    ],
    
    // Frequency units
    frequency: [
        { unit: "Hz", factor: 1, displayName: "Hertz" },
        { unit: "kHz", factor: 1000, displayName: "Kilohertz" },
        { unit: "MHz", factor: 1000000, displayName: "Megahertz" },
        { unit: "GHz", factor: 1000000000, displayName: "Gigahertz" },
    ],
    
    // Power units
    power: [
        { unit: "W", factor: 1, displayName: "Watts" },
        { unit: "kW", factor: 1000, displayName: "Kilowatts" },
        { unit: "MW", factor: 1000000, displayName: "Megawatts" },
        { unit: "GW", factor: 1000000000, displayName: "Gigawatts" },
    ],
    
    // Energy units
    energy: [
        { unit: "J", factor: 1, displayName: "Joules" },
        { unit: "kJ", factor: 1000, displayName: "Kilojoules" },
        { unit: "MJ", factor: 1000000, displayName: "Megajoules" },
        { unit: "W.h", factor: 3600, displayName: "Watt-Hours" },
        { unit: "kW.h", factor: 3600000, displayName: "Kilowatt-Hours" },
        { unit: "MW.h", factor: 3600000000, displayName: "Megawatt-Hours" },
    ],
    
    // Voltage units
    voltage: [
        { unit: "mV", factor: 0.001, displayName: "Millivolts" },
        { unit: "V", factor: 1, displayName: "Volts" },
        { unit: "kV", factor: 1000, displayName: "Kilovolts" },
    ],
    
    // Current units
    current: [
        { unit: "mA", factor: 0.001, displayName: "Milliamperes" },
        { unit: "A", factor: 1, displayName: "Amperes" },
    ],
    
    // Pressure units
    pressure: [
        { unit: "Pa", factor: 1, displayName: "Pascals" },
        { unit: "kPa", factor: 1000, displayName: "Kilopascals" },
        { unit: "MPa", factor: 1000000, displayName: "Megapascals" },
        { unit: "bar", factor: 100000, displayName: "Bars" },
        { unit: "mbar", factor: 100, displayName: "Millibars" },
    ],
    
    // Timestamp units (Unix timestamps)
    timestamp: [
        { unit: "unix_s", factor: 1, displayName: "Unix Timestamp (Seconds)" },
        { unit: "unix_ms", factor: 1000, displayName: "Unix Timestamp (Milliseconds)" },
        { unit: "unix_us", factor: 1000000, displayName: "Unix Timestamp (Microseconds)" },
        { unit: "unix_ns", factor: 1000000000, displayName: "Unix Timestamp (Nanoseconds)" },
    ],

    // Individual resource usage percentages (no conversion needed)
    resource_usage_cpu: [
        { unit: "%{CPU}", factor: 1, displayName: "CPU Usage %" },
    ],
    resource_usage_memory: [
        { unit: "%{memory}", factor: 1, displayName: "Memory Usage %" },
    ],
    resource_usage_disk: [
        { unit: "%{disk}", factor: 1, displayName: "Disk Usage %" },
    ],
    resource_usage_network: [
        { unit: "%{network}", factor: 1, displayName: "Network Usage %" },
    ],
    
    // Individual application metrics (no conversion between different types)
    requests_per_second: [
        { unit: "{requests}/s", factor: 1, displayName: "Requests Per Second" },
    ],
    errors_per_second: [
        { unit: "{errors}/s", factor: 1, displayName: "Errors Per Second" },
    ],
    ops_per_second: [
        { unit: "{ops}/s", factor: 1, displayName: "Operations Per Second" },
    ],
    transactions_per_second: [
        { unit: "{transactions}/s", factor: 1, displayName: "Transactions Per Second" },
    ],
    packets_per_second: [
        { unit: "{packets}/s", factor: 1, displayName: "Packets Per Second" },
    ],
    messages_per_second: [
        { unit: "{messages}/s", factor: 1, displayName: "Messages Per Second" },
    ],
    events_per_second: [
        { unit: "{events}/s", factor: 1, displayName: "Events Per Second" },
    ],
    
    // Individual system resources (no conversion between different types)
    connections_count: [
        { unit: "{connections}", factor: 1, displayName: "Active Connections" },
    ],
    threads_count: [
        { unit: "{threads}", factor: 1, displayName: "Active Threads" },
    ],
    processes_count: [
        { unit: "{processes}", factor: 1, displayName: "Active Processes" },
    ],
    users_count: [
        { unit: "{users}", factor: 1, displayName: "Active Users" },
    ],
    sessions_count: [
        { unit: "{sessions}", factor: 1, displayName: "Active Sessions" },
    ],
    cpu_cores_count: [
        { unit: "{CPU}", factor: 1, displayName: "CPU Cores" },
    ],
    
    // Percent
    percent: [
        { unit: "%", factor: 1, displayName: "Percent" },
    ],
    
    // Frequency rates
    frequency_rates: [
        { unit: "/s", factor: 1, displayName: "Per Second" },
        { unit: "/min", factor: 0.0166667, displayName: "Per Minute" },
        { unit: "/h", factor: 0.000277778, displayName: "Per Hour" },
        { unit: "/d", factor: 0.0000115741, displayName: "Per Day" },
    ],
    
    // Temperature units (each needs separate handling due to offset conversions)
    temperature_kelvin: [
        { unit: "K", factor: 1, displayName: "Kelvin" },
    ],
    temperature_celsius: [
        { unit: "Cel", factor: 1, displayName: "Celsius" },
    ],
    temperature_fahrenheit: [
        { unit: "[degF]", factor: 1, displayName: "Fahrenheit" },
    ],
    
    // Length imperial
    length_imperial: [
        { unit: "[in_i]", factor: 0.0254, displayName: "Inches" },
        { unit: "[ft_i]", factor: 0.3048, displayName: "Feet" },
        { unit: "[yd_i]", factor: 0.9144, displayName: "Yards" },
        { unit: "[mi_i]", factor: 1609.344, displayName: "Miles" },
    ],
    
    // Mass imperial
    mass_imperial: [
        { unit: "[oz_av]", factor: 28.34952, displayName: "Ounces" },
        { unit: "[lb_av]", factor: 453.59237, displayName: "Pounds" },
    ],
    
    // Area metric
    area_metric: [
        { unit: "mm2", factor: 0.000001, displayName: "Square Millimeters" },
        { unit: "cm2", factor: 0.0001, displayName: "Square Centimeters" },
        { unit: "m2", factor: 1, displayName: "Square Meters" },
        { unit: "km2", factor: 1000000, displayName: "Square Kilometers" },
        { unit: "har", factor: 10000, displayName: "Hectares" },
    ],
    
    // Area imperial
    area_imperial: [
        { unit: "[sin_i]", factor: 0.00064516, displayName: "Square Inches" },
        { unit: "[sft_i]", factor: 0.092903, displayName: "Square Feet" },
    ],
    
    // Volume metric
    volume_metric: [
        { unit: "ml", factor: 0.001, displayName: "Milliliters" },
        { unit: "l", factor: 1, displayName: "Liters" },
        { unit: "m3", factor: 1000, displayName: "Cubic Meters" },
    ],
    
    // Volume imperial
    volume_imperial: [
        { unit: "[foz_us]", factor: 0.0295735, displayName: "Fluid Ounces (US)" },
        { unit: "[pt_us]", factor: 0.473176, displayName: "Pints (US)" },
        { unit: "[qt_us]", factor: 0.946353, displayName: "Quarts (US)" },
        { unit: "[gal_us]", factor: 3.78541, displayName: "Gallons (US)" },
    ],
    
    // Speed
    speed_metric: [
        { unit: "m/s", factor: 1, displayName: "Meters/Second" },
        { unit: "km/h", factor: 0.277778, displayName: "Kilometers/Hour" },
    ],
    
    // Speed imperial
    speed_imperial: [
        { unit: "[mi_i]/h", factor: 0.44704, displayName: "Miles/Hour" },
        { unit: "[kn_i]", factor: 0.514444, displayName: "Knots" },
    ],
    
    // Force
    force: [
        { unit: "N", factor: 1, displayName: "Newtons" },
        { unit: "kN", factor: 1000, displayName: "Kilonewtons" },
        { unit: "[lbf_av]", factor: 4.44822, displayName: "Pounds-Force" },
    ],
    
    // Pressure extended
    pressure_extended: [
        { unit: "atm", factor: 101325, displayName: "Atmospheres" },
        { unit: "mm[Hg]", factor: 133.322, displayName: "Millimeters of Mercury" },
        { unit: "[psi]", factor: 6894.76, displayName: "Pounds/Square Inch" },
    ],
    
    // Energy extended
    energy_extended: [
        { unit: "cal", factor: 4.184, displayName: "Calories" },
        { unit: "kcal", factor: 4184, displayName: "Kilocalories" },
        { unit: "[Btu_IT]", factor: 1055.06, displayName: "British Thermal Units" },
    ],
    
    // Power extended
    power_extended: [
        { unit: "[hp]", factor: 745.7, displayName: "Horsepower" },
    ],
    
    // Electric current extended
    current_extended: [
        { unit: "A.h", factor: 3600, displayName: "Ampere-Hours" },
        { unit: "mA.h", factor: 3.6, displayName: "Milliampere-Hours" },
    ],
    
    // Electric resistance
    resistance: [
        { unit: "Ohm", factor: 1, displayName: "Ohms" },
        { unit: "kOhm", factor: 1000, displayName: "Kilohms" },
        { unit: "MOhm", factor: 1000000, displayName: "Megohms" },
    ],
    
    // Electric capacitance
    capacitance: [
        { unit: "pF", factor: 0.000000000001, displayName: "Picofarads" },
        { unit: "nF", factor: 0.000000001, displayName: "Nanofarads" },
        { unit: "uF", factor: 0.000001, displayName: "Microfarads" },
        { unit: "F", factor: 1, displayName: "Farads" },
    ],
    
    // Electric inductance
    inductance: [
        { unit: "uH", factor: 0.000001, displayName: "Microhenries" },
        { unit: "mH", factor: 0.001, displayName: "Millihenries" },
        { unit: "H", factor: 1, displayName: "Henries" },
    ],
    
    // Electric charge
    charge: [
        { unit: "C", factor: 1, displayName: "Coulombs" },
    ],
    
    // Concentration
    concentration_molar: [
        { unit: "nmol/l", factor: 0.000000001, displayName: "Nanomoles/Liter" },
        { unit: "umol/l", factor: 0.000001, displayName: "Micromoles/Liter" },
        { unit: "mmol/l", factor: 0.001, displayName: "Millimoles/Liter" },
        { unit: "mol/l", factor: 1, displayName: "Moles/Liter" },
    ],
    
    // Concentration mass
    concentration_mass: [
        { unit: "ng/l", factor: 0.000000001, displayName: "Nanograms/Liter" },
        { unit: "ug/l", factor: 0.000001, displayName: "Micrograms/Liter" },
        { unit: "mg/l", factor: 0.001, displayName: "Milligrams/Liter" },
        { unit: "g/l", factor: 1, displayName: "Grams/Liter" },
    ],
    
    // Currency (major currencies with K/M/B scaling)
    currency_usd: [
        { unit: "{USD}", factor: 1, displayName: "US Dollars" },
    ],
    currency_eur: [
        { unit: "{EUR}", factor: 1, displayName: "Euros" },
    ],
    currency_gbp: [
        { unit: "{GBP}", factor: 1, displayName: "British Pounds" },
    ],
    currency_jpy: [
        { unit: "{JPY}", factor: 1, displayName: "Japanese Yen" },
    ],
    currency_cny: [
        { unit: "{CNY}", factor: 1, displayName: "Chinese Yuan" },
    ],
    currency_chf: [
        { unit: "{CHF}", factor: 1, displayName: "Swiss Francs" },
    ],
    currency_cad: [
        { unit: "{CAD}", factor: 1, displayName: "Canadian Dollars" },
    ],
    currency_aud: [
        { unit: "{AUD}", factor: 1, displayName: "Australian Dollars" },
    ],
    currency_nzd: [
        { unit: "{NZD}", factor: 1, displayName: "New Zealand Dollars" },
    ],
    currency_inr: [
        { unit: "{INR}", factor: 1, displayName: "Indian Rupees" },
    ],
    currency_krw: [
        { unit: "{KRW}", factor: 1, displayName: "South Korean Won" },
    ],
    currency_hkd: [
        { unit: "{HKD}", factor: 1, displayName: "Hong Kong Dollars" },
    ],
    currency_sgd: [
        { unit: "{SGD}", factor: 1, displayName: "Singapore Dollars" },
    ],
    currency_sek: [
        { unit: "{SEK}", factor: 1, displayName: "Swedish Kronor" },
    ],
    currency_nok: [
        { unit: "{NOK}", factor: 1, displayName: "Norwegian Kroner" },
    ],
    currency_dkk: [
        { unit: "{DKK}", factor: 1, displayName: "Danish Kroner" },
    ],
    currency_mxn: [
        { unit: "{MXN}", factor: 1, displayName: "Mexican Pesos" },
    ],
    currency_brl: [
        { unit: "{BRL}", factor: 1, displayName: "Brazilian Reais" },
    ],
    currency_rub: [
        { unit: "{RUB}", factor: 1, displayName: "Russian Rubles" },
    ],
    currency_zar: [
        { unit: "{ZAR}", factor: 1, displayName: "South African Rand" },
    ],
    currency_aed: [
        { unit: "{AED}", factor: 1, displayName: "UAE Dirhams" },
    ],
    currency_sar: [
        { unit: "{SAR}", factor: 1, displayName: "Saudi Riyals" },
    ],
    currency_try: [
        { unit: "{TRY}", factor: 1, displayName: "Turkish Lira" },
    ],
    currency_pln: [
        { unit: "{PLN}", factor: 1, displayName: "Polish Zloty" },
    ],
    currency_thb: [
        { unit: "{THB}", factor: 1, displayName: "Thai Baht" },
    ],
    currency_myr: [
        { unit: "{MYR}", factor: 1, displayName: "Malaysian Ringgit" },
    ],
    currency_idr: [
        { unit: "{IDR}", factor: 1, displayName: "Indonesian Rupiah" },
    ],
    currency_php: [
        { unit: "{PHP}", factor: 1, displayName: "Philippine Pesos" },
    ],
    currency_czk: [
        { unit: "{CZK}", factor: 1, displayName: "Czech Koruna" },
    ],
    currency_huf: [
        { unit: "{HUF}", factor: 1, displayName: "Hungarian Forint" },
    ],
    currency_ils: [
        { unit: "{ILS}", factor: 1, displayName: "Israeli Shekels" },
    ],
    currency_clp: [
        { unit: "{CLP}", factor: 1, displayName: "Chilean Pesos" },
    ],
    currency_ars: [
        { unit: "{ARS}", factor: 1, displayName: "Argentine Pesos" },
    ],
    currency_cop: [
        { unit: "{COP}", factor: 1, displayName: "Colombian Pesos" },
    ],
    
    // Currency rates (per time unit)
    currency_rate_usd: [
        { unit: "{USD}/s", factor: 1, displayName: "US Dollars/Second" },
        { unit: "{USD}/min", factor: 60, displayName: "US Dollars/Minute" },
        { unit: "{USD}/h", factor: 3600, displayName: "US Dollars/Hour" },
        { unit: "{USD}/d", factor: 86400, displayName: "US Dollars/Day" },
        { unit: "{USD}/mo", factor: 2592000, displayName: "US Dollars/Month" }, // ~30 days
    ]
};

// Map units to their hierarchy
const UNIT_TO_HIERARCHY: { [key: string]: string } = {
    // Information decimal (1000-based)
    "bit": "information_decimal", "kBy": "information_decimal", 
    "MBy": "information_decimal", "GBy": "information_decimal", "TBy": "information_decimal", "PBy": "information_decimal",
    
    // Information binary (1024-based) - By defaults to binary for backward compatibility
    "By": "information_binary", "KiBy": "information_binary", "MiBy": "information_binary",
    "GiBy": "information_binary", "TiBy": "information_binary", "PiBy": "information_binary",
    
    // Data rates (decimal)
    "bit/s": "data_rate_bits", "kbit/s": "data_rate_bits", "Mbit/s": "data_rate_bits", "Gbit/s": "data_rate_bits",
    "kBy/s": "data_rate_bytes", "MBy/s": "data_rate_bytes", "GBy/s": "data_rate_bytes",
    
    // Data rates (binary) - By/s defaults to binary for consistency with By
    "By/s": "data_rate_bytes_binary", "KiBy/s": "data_rate_bytes_binary", "MiBy/s": "data_rate_bytes_binary",
    "GiBy/s": "data_rate_bytes_binary", "TiBy/s": "data_rate_bytes_binary",
    
    // Time
    "ns": "time", "us": "time", "ms": "time", "s": "time", "min": "time", "h": "time", "d": "time", "wk": "time", "mo": "time", "a": "time",
    
    // Length
    "nm": "length_metric", "um": "length_metric", "mm": "length_metric", "cm": "length_metric", 
    "dm": "length_metric", "m": "length_metric", "km": "length_metric",
    
    // Mass
    "ng": "mass_metric", "ug": "mass_metric", "mg": "mass_metric", "g": "mass_metric", "kg": "mass_metric", "t": "mass_metric",
    
    // Frequency
    "Hz": "frequency", "kHz": "frequency", "MHz": "frequency", "GHz": "frequency",
    
    // Power
    "W": "power", "kW": "power", "MW": "power", "GW": "power",
    
    // Energy
    "J": "energy", "kJ": "energy", "MJ": "energy", "W.h": "energy", "kW.h": "energy", "MW.h": "energy",
    
    // Voltage
    "mV": "voltage", "V": "voltage", "kV": "voltage",
    
    // Current
    "mA": "current", "A": "current",
    
    // Pressure
    "Pa": "pressure", "kPa": "pressure", "MPa": "pressure", "bar": "pressure", "mbar": "pressure",
    
    // Timestamp
    "unix_s": "timestamp", "unix_ms": "timestamp", "unix_us": "timestamp", "unix_ns": "timestamp",

    // Resource usage percentages
    "%{CPU}": "resource_usage_cpu",
    "%{memory}": "resource_usage_memory",
    "%{disk}": "resource_usage_disk",
    "%{network}": "resource_usage_network",
    
    // Application metrics
    "{requests}/s": "requests_per_second",
    "{errors}/s": "errors_per_second",
    "{ops}/s": "ops_per_second",
    "{transactions}/s": "transactions_per_second",
    "{packets}/s": "packets_per_second",
    "{messages}/s": "messages_per_second",
    "{events}/s": "events_per_second",
    
    // System resources
    "{connections}": "connections_count",
    "{threads}": "threads_count",
    "{processes}": "processes_count",
    "{users}": "users_count",
    "{sessions}": "sessions_count",
    "{CPU}": "cpu_cores_count",
    
    // Percent
    "%": "percent",
    
    // Frequency rates
    "/s": "frequency_rates", "/min": "frequency_rates", "/h": "frequency_rates", "/d": "frequency_rates",
    
    // Temperature
    "K": "temperature_kelvin",
    "Cel": "temperature_celsius",
    "[degF]": "temperature_fahrenheit",
    
    // Length imperial
    "[in_i]": "length_imperial", "[ft_i]": "length_imperial", "[yd_i]": "length_imperial", "[mi_i]": "length_imperial",
    
    // Mass imperial  
    "[oz_av]": "mass_imperial", "[lb_av]": "mass_imperial",
    
    // Area
    "mm2": "area_metric", "cm2": "area_metric", "m2": "area_metric", "km2": "area_metric", "har": "area_metric",
    "[sin_i]": "area_imperial", "[sft_i]": "area_imperial",
    
    // Volume
    "ml": "volume_metric", "l": "volume_metric", "m3": "volume_metric",
    "[foz_us]": "volume_imperial", "[pt_us]": "volume_imperial", "[qt_us]": "volume_imperial", "[gal_us]": "volume_imperial",
    
    // Speed
    "m/s": "speed_metric", "km/h": "speed_metric",
    "[mi_i]/h": "speed_imperial", "[kn_i]": "speed_imperial",
    
    // Force
    "N": "force", "kN": "force", "[lbf_av]": "force",
    
    // Pressure extended
    "atm": "pressure", "mm[Hg]": "pressure", "[psi]": "pressure",
    
    // Energy extended
    "cal": "energy", "kcal": "energy", "[Btu_IT]": "energy",
    
    // Power extended
    "[hp]": "power",
    
    // Electric current extended
    "A.h": "current", "mA.h": "current",
    
    // Electric resistance
    "Ohm": "resistance", "kOhm": "resistance", "MOhm": "resistance",
    
    // Electric capacitance
    "F": "capacitance", "uF": "capacitance", "nF": "capacitance", "pF": "capacitance",
    
    // Electric inductance
    "H": "inductance", "mH": "inductance", "uH": "inductance",
    
    // Electric charge
    "C": "charge",
    
    // Concentration
    "mol/l": "concentration_molar", "mmol/l": "concentration_molar", "umol/l": "concentration_molar", "nmol/l": "concentration_molar",
    "g/l": "concentration_mass", "mg/l": "concentration_mass", "ug/l": "concentration_mass", "ng/l": "concentration_mass",
    
    // Currency
    "{USD}": "currency_usd", "{EUR}": "currency_eur", "{GBP}": "currency_gbp", "{JPY}": "currency_jpy",
    "{CNY}": "currency_cny", "{CHF}": "currency_chf", "{CAD}": "currency_cad", "{AUD}": "currency_aud",
    "{NZD}": "currency_nzd", "{INR}": "currency_inr", "{KRW}": "currency_krw", "{HKD}": "currency_hkd",
    "{SGD}": "currency_sgd", "{SEK}": "currency_sek", "{NOK}": "currency_nok", "{DKK}": "currency_dkk",
    "{MXN}": "currency_mxn", "{BRL}": "currency_brl", "{RUB}": "currency_rub", "{ZAR}": "currency_zar",
    "{AED}": "currency_aed", "{SAR}": "currency_sar", "{TRY}": "currency_try", "{PLN}": "currency_pln",
    "{THB}": "currency_thb", "{MYR}": "currency_myr", "{IDR}": "currency_idr", "{PHP}": "currency_php",
    "{CZK}": "currency_czk", "{HUF}": "currency_huf", "{ILS}": "currency_ils", "{CLP}": "currency_clp",
    "{ARS}": "currency_ars", "{COP}": "currency_cop",
    
    // Currency rates
    "{USD}/s": "currency_rate_usd", "{USD}/min": "currency_rate_usd", "{USD}/h": "currency_rate_usd",
    "{USD}/d": "currency_rate_usd", "{USD}/mo": "currency_rate_usd"
};

/**
 * Converts a value to the most appropriate human-readable unit
 * @param value The numeric value to convert
 * @param currentUnit The current UCUM unit
 * @returns ConvertedValue with the best unit for display
 */
export function convertToHumanReadableUnit(value: number, currentUnit: string): ConvertedValue {
    // Handle edge cases
    if (value === 0 || !isFinite(value)) {
        const isTimestamp = currentUnit.startsWith('unix_');
        return {
            value,
            unit: currentUnit,
            displayName: currentUnit === '1' ? '' : getDisplayName(currentUnit),
            isTimestamp
        };
    }
    
    // Handle dimensionless unit
    if (currentUnit === '1') {
        return {
            value,
            unit: '',
            displayName: ''
        };
    }
    
    // Check if this is a timestamp unit
    if (currentUnit.startsWith('unix_')) {
        return {
            value,
            unit: currentUnit,
            displayName: getDisplayName(currentUnit),
            isTimestamp: true
        };
    }
    
    // Check if we have a conversion hierarchy for this unit
    const hierarchyName = UNIT_TO_HIERARCHY[currentUnit];
    if (!hierarchyName) {
        // No conversion available, return original
        return {
            value,
            unit: currentUnit,
            displayName: getDisplayName(currentUnit)
        };
    }
    
    const hierarchy = UNIT_HIERARCHIES[hierarchyName as keyof typeof UNIT_HIERARCHIES];
    if (!hierarchy) {
        return {
            value,
            unit: currentUnit,
            displayName: getDisplayName(currentUnit)
        };
    }
    
    // Find current unit in hierarchy
    const currentUnitInfo = hierarchy.find(u => u.unit === currentUnit);
    if (!currentUnitInfo) {
        return {
            value,
            unit: currentUnit,
            displayName: getDisplayName(currentUnit)
        };
    }

    // Convert to base unit (smallest unit in hierarchy)
    const baseUnit = hierarchy[0];
    const valueInBaseUnit = value * currentUnitInfo.factor / baseUnit.factor;
    
    // Find the best unit to display (largest unit where value >= 1)
    let bestUnit = baseUnit;
    for (let i = hierarchy.length - 1; i >= 0; i--) {
        const unit = hierarchy[i];
        const convertedValue = valueInBaseUnit * baseUnit.factor / unit.factor;
        
        // Use this unit if the value is >= 1 (or if it's the smallest unit)
        if (convertedValue >= 1 || i === 0) {
            bestUnit = unit;
            break;
        }
    }
    
    // Calculate final converted value
    const convertedValue = valueInBaseUnit * baseUnit.factor / bestUnit.factor;

    return {
        value: convertedValue,
        unit: bestUnit.unit,
        displayName: bestUnit.displayName
    };
}

/**
 * Format unitless values with proper scale prefixes (K, M, B, T)
 */
function formatUnitlessValue(value: number, precision: number = 2): string {
    const absValue = Math.abs(value);
    
    if (absValue === 0) return "0";
    if (absValue < 1) return value.toFixed(precision);
    
    const scales = [
        { threshold: 1e12, suffix: 'T', divisor: 1e12 },
        { threshold: 1e9, suffix: 'G', divisor: 1e9 },
        { threshold: 1e6, suffix: 'M', divisor: 1e6 },
        { threshold: 1e3, suffix: 'K', divisor: 1e3 },
    ];
    
    for (const scale of scales) {
        if (absValue >= scale.threshold) {
            const scaledValue = value / scale.divisor;
            // Format based on magnitude
            let formatted: string;
            if (Math.abs(scaledValue) >= 100) {
                formatted = Math.round(scaledValue).toString();
            } else if (Math.abs(scaledValue) >= 10) {
                formatted = scaledValue.toFixed(1);
            } else {
                formatted = scaledValue.toFixed(precision);
            }
            return `${formatted}${scale.suffix}`;
        }
    }
    
    // For values less than 1000, just format normally
    return Math.round(value).toString();
}

/**
 * Format currency values with proper scale prefixes (K, M, B, T)
 */
function formatCurrencyValue(value: number, currencyUnit: string, precision: number = 2): string {
    const absValue = Math.abs(value);
    
    if (absValue === 0) return `0 ${currencyUnit}`;
    if (absValue < 1000) return `${value.toFixed(precision)} ${currencyUnit}`;
    
    const scales = [
        { threshold: 1e12, suffix: 'T', divisor: 1e12 },
        { threshold: 1e9, suffix: 'B', divisor: 1e9 },
        { threshold: 1e6, suffix: 'M', divisor: 1e6 },
        { threshold: 1e3, suffix: 'K', divisor: 1e3 },
    ];
    
    for (const scale of scales) {
        if (absValue >= scale.threshold) {
            const scaledValue = value / scale.divisor;
            // Format based on magnitude
            let formatted: string;
            if (Math.abs(scaledValue) >= 100) {
                formatted = Math.round(scaledValue).toString();
            } else if (Math.abs(scaledValue) >= 10) {
                formatted = scaledValue.toFixed(1);
            } else {
                formatted = scaledValue.toFixed(precision);
            }
            return `${formatted}${scale.suffix} ${currencyUnit}`;
        }
    }
    
    return `${value.toFixed(precision)} ${currencyUnit}`;
}

/**
 * Formats a value with appropriate unit conversion for display
 * @param value The numeric value
 * @param unit The UCUM unit
 * @param precision Number of decimal places (default: 2)
 * @param options Additional formatting options
 * @returns Formatted string like "1.5 MB" or "2.3 GB" or formatted datetime
 */
export function formatValueWithUnit(
    value: number, 
    unit: string, 
    precision: number = 2,
    options?: { 
        shortFormat?: boolean;
        includeTime?: boolean;
        includeMillis?: boolean;
    }
): string {
    // Handle empty or missing units or dimensionless unit
    if (!unit || unit.trim() === '' || unit === '1') {
        return formatUnitlessValue(value, precision);
    }
    
    const converted = convertToHumanReadableUnit(value, unit);
    
    // Handle timestamp formatting
    if (converted.isTimestamp) {
        return formatTimestamp(value, unit, {
            short: options?.shortFormat,
            includeTime: options?.includeTime,
            includeMillis: options?.includeMillis
        });
    }
    
    // Format the number based on its magnitude
    let formattedValue: string;
    if (converted.value === 0) {
        formattedValue = "0";
    } else if (converted.value >= 100) {
        formattedValue = Math.round(converted.value).toString();
    } else if (converted.value >= 10) {
        formattedValue = converted.value.toFixed(1);
    } else {
        formattedValue = converted.value.toFixed(precision);
    }
    
    return `${formattedValue} ${converted.displayName}`;
}

/**
 * Determine the best unit for displaying a range of values
 * @param min Minimum value in the range
 * @param max Maximum value in the range  
 * @param currentUnit The current UCUM unit
 * @returns The best unit and scale factor for display
 */
export function getBestUnitForRange(min: number, max: number, currentUnit: string): { unit: string; displayName: string; scaleFactor: number } {
    // Handle edge cases
    if (!isFinite(max) || !isFinite(min)) {
        return { unit: currentUnit, displayName: '', scaleFactor: 1 };
    }
    
    // Handle unitless values or dimensionless unit
    if (!currentUnit || currentUnit.trim() === '' || currentUnit === '1') {
        // For unitless/dimensionless values, don't show any unit on the axis
        // The K/M/B/T formatting will be applied to the tick values instead
        return { unit: '', displayName: '', scaleFactor: 1 };
    }
    
    // Don't convert timestamps
    if (currentUnit.startsWith('unix_')) {
        return { unit: currentUnit, displayName: getDisplayName(currentUnit), scaleFactor: 1 };
    }
    
    // Use the maximum absolute value to determine the best unit
    const absMax = Math.max(Math.abs(min), Math.abs(max));
    
    // Convert to human readable unit based on the max value
    const converted = convertToHumanReadableUnit(absMax, currentUnit);
    
    // Calculate the scale factor
    // We need to figure out how to convert from the original unit to the new unit
    const hierarchyName = UNIT_TO_HIERARCHY[currentUnit];
    if (!hierarchyName) {
        // For units without a hierarchy (like %), return the original unit with no scaling
        return { unit: currentUnit, displayName: getDisplayName(currentUnit), scaleFactor: 1 };
    }
    
    const hierarchy = UNIT_HIERARCHIES[hierarchyName as keyof typeof UNIT_HIERARCHIES];
    if (!hierarchy) {
        return { unit: currentUnit, displayName: getDisplayName(currentUnit), scaleFactor: 1 };
    }
    
    // Find the original and target units in the hierarchy
    const originalUnit = hierarchy.find(u => u.unit === currentUnit);
    const targetUnit = hierarchy.find(u => u.unit === converted.unit);
    
    if (!originalUnit || !targetUnit) {
        return { unit: currentUnit, displayName: getDisplayName(currentUnit), scaleFactor: 1 };
    }
    
    // Calculate scale factor: how many original units equal one target unit
    // For example: if original is By (factor=1) and target is Kiby (factor=1024),
    // then scaleFactor = 1024 (meaning 1024 bytes = 1 KiB)
    const scaleFactor = targetUnit.factor / originalUnit.factor;
    
    // Get a more readable display name for the y-axis
    let axisDisplayName = converted.displayName;
    
    // For common units, use more descriptive names that are still concise
    const readableNames: { [key: string]: string } = {
        // Information units
        'B': 'Bytes',
        'KiB': 'KiB',
        'MiB': 'MiB', 
        'GiB': 'GiB',
        'TiB': 'TiB',
        'PiB': 'PiB',
        'kB': 'kB',
        'MB': 'MB',
        'GB': 'GB',
        'TB': 'TB',
        'PB': 'PB',
        // Time units
        'ns': 'Nanoseconds',
        'us': 'Microseconds',
        'ms': 'Milliseconds',
        's': 'Seconds',
        'min': 'Minutes',
        'h': 'Hours',
        'd': 'Days',
        // Unix timestamps
        'unix_s': 'Unix Timestamp',
        'unix_ms': 'Unix Timestamp (ms)',
        'unix_us': 'Unix Timestamp (μs)',
        'unix_ns': 'Unix Timestamp (ns)',
        // Frequency
        'Hz': 'Hz',
        'kHz': 'kHz',
        'MHz': 'MHz',
        'GHz': 'GHz',
        // Other common units
        '%': 'Percent',
        'W': 'Watts',
        'kW': 'kW',
        'MW': 'MW',
        'GW': 'GW',
        // Data rates
        'B/s': 'B/s',
        'KiB/s': 'KiB/s',
        'MiB/s': 'MiB/s',
        'GiB/s': 'GiB/s',
        'kB/s': 'kB/s',
        'MB/s': 'MB/s',
        'GB/s': 'GB/s',
        'bit/s': 'bit/s',
        'kbit/s': 'kbit/s',
        'Mbit/s': 'Mbit/s',
        'Gbit/s': 'Gbit/s',
        // Application metrics
        'requests/s': 'Requests/s',
        'errors/s': 'Errors/s',
        'ops/s': 'Operations/s',
        'transactions/s': 'Transactions/s',
        'packets/s': 'Packets/s',
        'messages/s': 'Messages/s',
        'events/s': 'Events/s',
        // System resources
        'connections': 'Connections',
        'threads': 'Threads',
        'processes': 'Processes',
        'users': 'Users',
        'sessions': 'Sessions',
        'CPU cores': 'CPU Cores',
        // Resource usage percentages
        '% CPU': 'CPU %',
        '% memory': 'Memory %',
        '% disk': 'Disk %',
        '% network': 'Network %',
        // Temperature
        'K': 'Kelvin',
        '°C': 'Celsius',
        '°F': 'Fahrenheit',
        // Length
        'in': 'Inches',
        'ft': 'Feet',
        'yd': 'Yards',
        'mi': 'Miles',
        // Mass
        'oz': 'Ounces',
        'lb': 'Pounds',
        // Area
        'mm²': 'mm²',
        'cm²': 'cm²',
        'm²': 'm²',
        'km²': 'km²',
        'ha': 'Hectares',
        'in²': 'in²',
        'ft²': 'ft²',
        // Volume
        'ml': 'Milliliters',
        'l': 'Liters',
        'm³': 'm³',
        'fl oz': 'Fluid Ounces',
        'pt': 'Pints',
        'qt': 'Quarts',
        'gal': 'Gallons',
        // Speed
        'm/s': 'm/s',
        'km/h': 'km/h',
        'mph': 'mph',
        'kn': 'Knots',
        // Force
        'N': 'Newtons',
        'kN': 'kN',
        'lbf': 'Pounds Force',
        // Pressure
        'Pa': 'Pascals',
        'kPa': 'kPa',
        'MPa': 'MPa',
        'bar': 'Bar',
        'mbar': 'mbar',
        'atm': 'Atmospheres',
        'mmHg': 'mmHg',
        'psi': 'PSI',
        // Energy
        'J': 'Joules',
        'kJ': 'kJ',
        'MJ': 'MJ',
        'Wh': 'Wh',
        'kWh': 'kWh',
        'MWh': 'MWh',
        'cal': 'Calories',
        'kcal': 'kcal',
        'BTU': 'BTU',
        // Power
        'hp': 'Horsepower',
        // Electric
        'V': 'Volts',
        'mV': 'mV',
        'kV': 'kV',
        'A': 'Amperes',
        'mA': 'mA',
        'Ah': 'Ah',
        'mAh': 'mAh',
        'Ω': 'Ohms',
        'kΩ': 'kΩ',
        'MΩ': 'MΩ',
        'F': 'Farads',
        'μF': 'μF',
        'nF': 'nF',
        'pF': 'pF',
        'H': 'Henries',
        'mH': 'mH',
        'μH': 'μH',
        'C': 'Coulombs',
        // Concentration
        'mol/l': 'mol/l',
        'mmol/l': 'mmol/l',
        'μmol/l': 'μmol/l',
        'nmol/l': 'nmol/l',
        'g/l': 'g/l',
        'mg/l': 'mg/l',
        'μg/l': 'μg/l',
        'ng/l': 'ng/l',
        // Frequency rates
        '/s': 'Per Second',
        '/min': 'Per Minute',
        '/h': 'Per Hour',
        '/d': 'Per Day',
        // Currency
        'USD': 'USD',
        'EUR': 'EUR',
        'GBP': 'GBP',
        'JPY': 'JPY',
        'CNY': 'CNY',
        'CHF': 'CHF',
        'CAD': 'CAD',
        'AUD': 'AUD',
        'NZD': 'NZD',
        'INR': 'INR',
        'KRW': 'KRW',
        'HKD': 'HKD',
        'SGD': 'SGD',
        'SEK': 'SEK',
        'NOK': 'NOK',
        'DKK': 'DKK',
        'MXN': 'MXN',
        'BRL': 'BRL',
        'RUB': 'RUB',
        'ZAR': 'ZAR',
        'AED': 'AED',
        'SAR': 'SAR',
        'TRY': 'TRY',
        'PLN': 'PLN',
        'THB': 'THB',
        'MYR': 'MYR',
        'IDR': 'IDR',
        'PHP': 'PHP',
        'CZK': 'CZK',
        'HUF': 'HUF',
        'ILS': 'ILS',
        'CLP': 'CLP',
        'ARS': 'ARS',
        'COP': 'COP',
        // Currency rates
        'USD/s': 'USD/s',
        'USD/min': 'USD/min',
        'USD/h': 'USD/h',
        'USD/d': 'USD/d',
        'USD/mo': 'USD/mo',
    };
    
    if (readableNames[axisDisplayName]) {
        axisDisplayName = readableNames[axisDisplayName];
    }
    
    return {
        unit: converted.unit,
        displayName: axisDisplayName,
        scaleFactor: scaleFactor
    };
}

/**
 * Get display name for a unit, fallback to unit code if not found
 */
function getDisplayName(unit: string, fullName: boolean = false): string {
    // Search through all categories to find the display name
    for (const category of UCUM_CATEGORIES) {
        const foundUnit = category.items.find(item => item.value === unit);
        if (foundUnit && foundUnit.displayName) {
            if (fullName) {
                // Return the full display name for axis labels
                return foundUnit.displayName;
            } else {
                // Extract just the unit part from display name like "kB (kilobyte)" -> "kB"
                const match = foundUnit.displayName.match(/^([^\s(]+)/);
                return match ? match[1] : unit;
            }
        }
    }
    return unit;
}

/**
 * Generate nice axis tick values for a given range and unit
 * @param min Minimum value in the range
 * @param max Maximum value in the range
 * @param unit The UCUM unit
 * @param maxTicks Maximum number of ticks (default: 6)
 * @returns Array of tick values with converted units
 */
export function generateAxisTicks(min: number, max: number, unit: string, maxTicks: number = 6): ConvertedValue[] {
    if (min === max) {
        return [convertToHumanReadableUnit(min, unit)];
    }
    
    // Special handling for timestamps
    if (unit.startsWith('unix_')) {
        const ticks: ConvertedValue[] = [];
        const tickCount = Math.min(maxTicks, 6);
        const step = (max - min) / (tickCount - 1);
        
        for (let i = 0; i < tickCount; i++) {
            const value = min + (step * i);
            ticks.push({
                value,
                unit,
                displayName: formatTimestamp(value, unit, { short: true }),
                isTimestamp: true
            });
        }
        
        return ticks;
    }
    
    const range = max - min;
    const rawStep = range / (maxTicks - 1);
    
    // Round step to a nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;
    
    let niceStep: number;
    if (normalizedStep <= 1) niceStep = 1;
    else if (normalizedStep <= 2) niceStep = 2;
    else if (normalizedStep <= 5) niceStep = 5;
    else niceStep = 10;
    
    const step = niceStep * magnitude;
    
    // Generate tick values
    const ticks: ConvertedValue[] = [];
    const niceMin = Math.floor(min / step) * step;
    
    for (let value = niceMin; value <= max + step * 0.001; value += step) {
        if (value >= min) {
            ticks.push(convertToHumanReadableUnit(value, unit));
        }
    }
    
    return ticks;
}